/**
 * Coups de pouce — Attendu 658 « Définition de la médiatrice d'un segment ».
 *
 * Une entrée par `generator_key` (cf. gen_658.js). Contrat d'une entrée :
 *   pages : 2 pages MAXIMUM, chacune { titre, lignes?, svg? }.
 *           Page 1 « La règle »   : les phrases du mini-cours.
 *           Page 2 « Un exemple » : la figure qui les illustre.
 *
 * Règle absolue : l'aide porte sur d'AUTRES lettres et d'AUTRES configurations
 * que celles que le générateur peut tirer, sinon elle donne la réponse. Chaque
 * coup de pouce est autonome — les formats sont indépendants et un exercice
 * peut ne jamais être donné : la définition est donc répétée dans les deux.
 * Emphase : **texte** (rendu en gras, pas de HTML brut). Pas de tiret cadratin.
 */

// ── Figure Ex. 1 : la médiatrice et la lecture de son codage ──
// L'exercice fait juger des affirmations sur une figure CODÉE : l'élève doit
// savoir que le petit carré vaut « angle droit » et que deux traits identiques
// valent « mêmes longueurs ». La figure montre donc le cas médiatrice avec ses
// deux codages nommés, plutôt qu'un cas à trancher — le régime tiré varie, la
// convention de lecture non.
const SVG_CODAGE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 200" role="img"
     aria-label="Un segment RS de milieu M et une droite d qui passe par M perpendiculairement au segment : d est la médiatrice de RS">
  <line x1="160" y1="18" x2="160" y2="118" stroke="#378ADD" stroke-width="2.5"/>
  <text x="168" y="31" font-size="12.5" font-style="italic" font-weight="700" fill="#378ADD">(d)</text>

  <line x1="70" y1="86" x2="250" y2="86" stroke="#0f172a" stroke-width="2.5" stroke-linecap="round"/>
  <line x1="115" y1="79" x2="115" y2="93" stroke="#E24B4A" stroke-width="2"/>
  <line x1="205" y1="79" x2="205" y2="93" stroke="#E24B4A" stroke-width="2"/>
  <polyline points="172,86 172,74 160,74" fill="none" stroke="#F97316" stroke-width="1.8"/>

  <circle cx="70" cy="86" r="4" fill="#0f172a"/>
  <circle cx="250" cy="86" r="4" fill="#0f172a"/>
  <circle cx="160" cy="86" r="4" fill="#0f172a"/>
  <text x="62" y="105" font-size="12.5" font-weight="700" fill="#0f172a">R</text>
  <text x="256" y="105" font-size="12.5" font-weight="700" fill="#0f172a">S</text>
  <text x="167" y="106" font-size="12.5" font-weight="700" fill="#0f172a">M</text>

  <text x="112" y="66" text-anchor="middle" font-size="11" font-weight="700" fill="#E24B4A">RM = MS</text>
  <text x="206" y="66" text-anchor="middle" font-size="11" font-weight="700" fill="#F97316">angle droit</text>

  <text x="180" y="143" text-anchor="middle" font-size="11.5" fill="#334155">(d) passe par le milieu M de [RS]</text>
  <text x="180" y="161" text-anchor="middle" font-size="11.5" fill="#334155">(d) est perpendiculaire à [RS]</text>
  <text x="180" y="186" text-anchor="middle" font-size="12.5" font-weight="700" fill="#0f172a">donc (d) est la médiatrice de [RS]</text>
</svg>`.trim();

// ── Figure Ex. 2 : les deux presque-médiatrices ──
// L'exercice fait choisir parmi quatre droites, dont exactement une remplit les
// deux conditions. Les deux pièges sont montrés CÔTE À CÔTE, parce que c'est
// leur comparaison qui apprend à éliminer : chacun pris seul semble acceptable.
// La bonne droite n'est pas dessinée ici, elle est décrite dans la règle.
const SVG_DEUX_PIEGES = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 200" role="img"
     aria-label="À gauche une droite qui passe par le milieu du segment RS mais qui penche ; à droite une droite perpendiculaire au segment TU mais décalée du milieu. Aucune des deux n'est la médiatrice.">
  <line x1="180" y1="28" x2="180" y2="152" stroke="#E2E8F0" stroke-width="1"/>

  <line x1="58" y1="96" x2="132" y2="44" stroke="#378ADD" stroke-width="2.5"/>
  <line x1="35" y1="70" x2="155" y2="70" stroke="#0f172a" stroke-width="2.5" stroke-linecap="round"/>
  <line x1="80" y1="63" x2="80" y2="77" stroke="#E24B4A" stroke-width="2"/>
  <line x1="110" y1="63" x2="110" y2="77" stroke="#E24B4A" stroke-width="2"/>
  <circle cx="35" cy="70" r="4" fill="#0f172a"/>
  <circle cx="155" cy="70" r="4" fill="#0f172a"/>
  <text x="27" y="88" font-size="11.5" font-weight="700" fill="#0f172a">R</text>
  <text x="160" y="88" font-size="11.5" font-weight="700" fill="#0f172a">S</text>
  <text x="95" y="126" text-anchor="middle" font-size="11" fill="#334155">passe par le milieu,</text>
  <text x="95" y="142" text-anchor="middle" font-size="11" font-weight="700" fill="#334155">mais elle penche</text>

  <line x1="300" y1="32" x2="300" y2="108" stroke="#378ADD" stroke-width="2.5"/>
  <line x1="205" y1="70" x2="325" y2="70" stroke="#0f172a" stroke-width="2.5" stroke-linecap="round"/>
  <line x1="250" y1="63" x2="250" y2="77" stroke="#E24B4A" stroke-width="2"/>
  <line x1="280" y1="63" x2="280" y2="77" stroke="#E24B4A" stroke-width="2"/>
  <polyline points="312,70 312,58 300,58" fill="none" stroke="#F97316" stroke-width="1.8"/>
  <circle cx="205" cy="70" r="4" fill="#0f172a"/>
  <circle cx="325" cy="70" r="4" fill="#0f172a"/>
  <text x="197" y="88" font-size="11.5" font-weight="700" fill="#0f172a">T</text>
  <text x="330" y="88" font-size="11.5" font-weight="700" fill="#0f172a">U</text>
  <text x="265" y="126" text-anchor="middle" font-size="11" fill="#334155">perpendiculaire,</text>
  <text x="265" y="142" text-anchor="middle" font-size="11" font-weight="700" fill="#334155">mais pas au milieu</text>

  <text x="180" y="180" text-anchor="middle" font-size="12.5" font-weight="700" fill="#0f172a">il faut les deux conditions à la fois</text>
</svg>`.trim();

// ── Figure Ex. 4 : la fiche à deux conditions ──
// L'exercice fait compléter la définition : toute règle écrite donnerait les
// mots à placer. L'aide reste donc une figure. La figure est épurée, et les
// deux conditions sont listées dessous, numérotées, chacune dans la couleur de
// son codage : l'orange renvoie au carré d'angle droit, le rouge aux deux
// marques de milieu. Aucun trait de rappel, aucune annotation flottante.
const SVG_VOCABULAIRE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 230" role="img"
     aria-label="Un segment RS et la droite d qui le coupe en son milieu en formant un angle droit. Dessous, les deux conditions numerotees : perpendiculaire, et passe par le milieu. Les deux ensemble font l'axe de symetrie.">
  <line x1="180" y1="20" x2="180" y2="118" stroke="#378ADD" stroke-width="2.5"/>
  <text x="188" y="33" font-size="12.5" font-style="italic" font-weight="700" fill="#378ADD">(d)</text>

  <line x1="90" y1="88" x2="270" y2="88" stroke="#0f172a" stroke-width="2.5" stroke-linecap="round"/>
  <line x1="135" y1="81" x2="135" y2="95" stroke="#E24B4A" stroke-width="2"/>
  <line x1="225" y1="81" x2="225" y2="95" stroke="#E24B4A" stroke-width="2"/>
  <polyline points="192,88 192,76 180,76" fill="none" stroke="#F97316" stroke-width="1.8"/>

  <circle cx="90" cy="88" r="4" fill="#0f172a"/>
  <circle cx="270" cy="88" r="4" fill="#0f172a"/>
  <circle cx="180" cy="88" r="4" fill="#0f172a"/>
  <text x="81" y="107" font-size="12.5" font-weight="700" fill="#0f172a">R</text>
  <text x="275" y="107" font-size="12.5" font-weight="700" fill="#0f172a">S</text>

  <circle cx="44" cy="150" r="11" fill="#F97316"/>
  <text x="44" y="155" text-anchor="middle" font-size="13" font-weight="700" fill="#ffffff">1</text>
  <text x="64" y="155" font-size="13" font-weight="700" fill="#F97316">perpendiculaire</text>

  <circle cx="44" cy="184" r="11" fill="#E24B4A"/>
  <text x="44" y="189" text-anchor="middle" font-size="13" font-weight="700" fill="#ffffff">2</text>
  <text x="64" y="189" font-size="13" font-weight="700" fill="#E24B4A">passe par le milieu</text>

  <text x="180" y="219" text-anchor="middle" font-size="12.5" font-weight="700" fill="#0f172a">les deux ensemble = axe de symétrie</text>
</svg>`.trim();

// ── Figure Ex. 5 : le pli ──
// L'exercice fait reconnaître qu'une même situation se dit de plusieurs façons.
// Le pli est ce qui rend l'équivalence évidente : si on plie la feuille le long
// de la droite, R vient exactement sur S. Aucune des formulations du tirage
// n'est reprise telle quelle, c'est le geste qui est montré.
const SVG_PLI = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 190" role="img"
     aria-label="Un segment RS et sa mediatrice d. Une fleche courbe montre que si l'on plie la feuille le long de d, le point R vient exactement sur le point S.">
  <line x1="180" y1="15" x2="180" y2="150" stroke="#378ADD" stroke-width="2.5"/>
  <text x="188" y="146" font-size="12.5" font-style="italic" font-weight="700" fill="#378ADD">(d)</text>

  <path d="M95,88 Q180,25 265,88" fill="none" stroke="#9333EA" stroke-width="1.8" stroke-dasharray="5 4"/>
  <polyline points="260.7,80.1 265,88 256.2,86.2" fill="none" stroke="#9333EA" stroke-width="1.8" stroke-linecap="round"/>
  <text x="120" y="40" text-anchor="middle" font-size="11.5" font-weight="700" fill="#9333EA">R vient sur S</text>

  <line x1="95" y1="95" x2="265" y2="95" stroke="#0f172a" stroke-width="2.5" stroke-linecap="round"/>
  <line x1="137" y1="88" x2="137" y2="102" stroke="#E24B4A" stroke-width="2"/>
  <line x1="223" y1="88" x2="223" y2="102" stroke="#E24B4A" stroke-width="2"/>
  <polyline points="192,95 192,83 180,83" fill="none" stroke="#F97316" stroke-width="1.8"/>

  <circle cx="95" cy="95" r="4" fill="#0f172a"/>
  <circle cx="265" cy="95" r="4" fill="#0f172a"/>
  <circle cx="180" cy="95" r="4" fill="#0f172a"/>
  <text x="86" y="113" font-size="12.5" font-weight="700" fill="#0f172a">R</text>
  <text x="270" y="113" font-size="12.5" font-weight="700" fill="#0f172a">S</text>

  <text x="180" y="176" text-anchor="middle" font-size="12.5" font-weight="700" fill="#0f172a">(d) est l'axe de symétrie de [RS]</text>
</svg>`.trim();

const CP_658 = {
    mediatrice_vf_reconnaissance: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    '**Médiatrice de [AB]** : la droite qui passe par le **milieu** de [AB] **et** qui est **perpendiculaire** à [AB].',
                    'Les deux conditions ensemble. Une seule ne suffit pas.',
                    'Sur une figure, le petit carré signale l\'angle droit, les deux traits identiques signalent que les deux moitiés sont égales.',
                    'La médiatrice de [AB] est aussi son axe de symétrie : A et B sont symétriques par rapport à elle.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_CODAGE },
        ],
    },

    mediatrice_qcm_identifier_figure: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    '**Médiatrice de [AB]** : la droite qui passe par le **milieu** de [AB] **et** qui est **perpendiculaire** à [AB].',
                    'Teste les deux conditions l\'une après l\'autre.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_DEUX_PIEGES },
        ],
    },

    // Définition verbale, sans figure : on reprend la règle de l'Ex. 1 privée de
    // ses deux dernières lignes. La lecture du codage et l'axe de symétrie n'ont
    // pas d'objet ici, l'exercice ne porte que sur les deux conditions.
    mediatrice_qcm_definition: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    '**Médiatrice de [AB]** : la droite qui passe par le **milieu** de [AB] **et** qui est **perpendiculaire** à [AB].',
                    'Les deux conditions ensemble. Une seule ne suffit pas.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_CODAGE },
        ],
    },

    // Aucune règle écrite : la définition à compléter EST l'exercice. L'aide se
    // limite à la figure et à la liste numérotée des deux conditions.
    mediatrice_vocabulaire_trous: {
        pages: [
            { titre: 'Un exemple', svg: SVG_VOCABULAIRE },
        ],
    },

    // Reconnaître qu'une même situation se dit de plusieurs façons : la règle
    // donne les trois registres (perpendiculaire + milieu, axe de symétrie,
    // points symétriques), la figure montre le pli qui les relie.
    mediatrice_notations_vf: {
        pages: [
            {
                titre: 'La règle',
                // Ancienne version : les trois lignes énonçaient la propriété et
                // le vocabulaire, ce qui répondait à quatre lignes du tableau sur
                // six. On garde le geste du pli, on retire les conclusions.
                lignes: [
                    'Plie la feuille le long de la droite. Les deux extrémités du segment se posent-elles exactement l\'une sur l\'autre ?',
                    'Deux conditions doivent tenir **ensemble** : l\'angle droit, et le passage par le point qui coupe le segment en deux parts égales. Teste-les l\'une après l\'autre.',
                    'Une affirmation qui n\'en retient qu\'une seule décrit donc autre chose : regarde laquelle elle laisse tomber.',
                    '« Symétrique » et « image » nomment la même idée : le point d\'arrivée après le pli. Vérifie où chaque point atterrit avant de trancher.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_PLI },
        ],
    },
};

export default CP_658;
